import { format } from "date-fns";

const ProposalPreview = ({ proposal, settings, logoDataUrl, exchangeRates }) => {
  const formatCurrency = (amount, currency = "USD") => {
    return `${currency} ${(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const toINR = (amount, currency) => {
    if (proposal.show_inr === false) return null;
    if (!amount || currency === "INR" || !exchangeRates?.[currency]) return null;
    return Math.round(parseFloat(amount) * exchangeRates[currency]);
  };

  const fmtINR = (amount) => {
    if (!amount) return "";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);
  };

  const fmtDate = (d) => {
    if (!d) return "";
    return format(new Date(d), "dd MMM yyyy");
  };

  const dayName = (d) => {
    if (!d) return "";
    return format(new Date(d), "EEEE");
  };

  const getAllInclusions = () => {
    const inclusions = [...(proposal.inclusions || [])];
    if (proposal.custom_inclusions) {
      proposal.custom_inclusions.split("\n").filter(Boolean).forEach((i) => {
        inclusions.push(i.trim());
      });
    }
    return inclusions;
  };

  const agencyName = settings?.agency_name || "Muskan Travel Associates Private Limited";
  const agencyTagline = settings?.agency_tagline || "";

  return (
    <div className="font-serif" data-testid="proposal-preview">
      {/* Header */}
      <div className="bg-navy p-8 pb-7 relative min-h-[110px]">
        {logoDataUrl && (
          <img src={logoDataUrl} alt="Logo" className="absolute top-3 right-6 w-28 h-28 object-contain" />
        )}
        <p className="text-[10px] font-sans uppercase tracking-[0.2em] text-gold mb-1">Booking Proposal</p>
        <h1 className="text-2xl text-white">{proposal.trip_title || "Multi-City Hotel Proposal"}</h1>
        <p className="text-[10px] font-sans text-white/40 mt-2 uppercase tracking-wider">
          {agencyTagline ? `${agencyName} · ${agencyTagline}` : agencyName}
        </p>
      </div>
      <div className="h-1 bg-gold"></div>

      {/* Meta Bar */}
      <div className="bg-cream p-4 flex gap-8 border-b border-border font-sans">
        <div>
          <p className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground mb-1">Prepared for</p>
          <p className="text-sm font-bold text-navy">{proposal.client_name || "—"}</p>
        </div>
        {proposal.client_email && (
          <div>
            <p className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground mb-1">Email</p>
            <p className="text-sm font-bold text-navy">{proposal.client_email}</p>
          </div>
        )}
        <div>
          <p className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground mb-1">Properties</p>
          <p className="text-sm font-bold text-navy">{proposal.hotels?.length || 0}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground mb-1">Ref</p>
          <p className="text-sm font-bold text-navy">{proposal.reference}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground mb-1">Date</p>
          <p className="text-sm font-bold text-navy">{fmtDate(proposal.proposal_date)}</p>
        </div>
      </div>

      {/* Hotels */}
      {proposal.hotels?.map((hotel, hotelIndex) => {
        const amenities = hotel.amenities?.split(",").map((a) => a.trim()).filter(Boolean) || [];
        const cityText = [hotel.city, hotel.country].filter(Boolean).join(", ");
        return (
          <div key={hotel.id} className="p-8 border-b border-border">
            {/* Hotel banner */}
            <div className="bg-navy p-4 mb-5 flex items-center justify-between" data-testid={`pdf-hotel-banner-${hotelIndex}`}>
              <div>
                <p className="text-[9px] font-sans uppercase tracking-[0.15em] text-gold mb-1">
                  Proposed Stay · {hotelIndex + 1} of {proposal.hotels.length}
                </p>
                <h2 className="text-lg font-bold text-white">{hotel.hotel_name}</h2>
              </div>
              {cityText && <p className="text-[10px] font-sans text-white/50">{cityText}</p>}
            </div>
            {/* Inline hero image (only when NOT separate page) */}
            {(() => {
              const imgs = hotel.images || [];
              if (!imgs.length || hotel.images_separate_page) return null;
              const hero = imgs.find((i) => i.is_hero) || imgs[0];
              return <img src={hero.url} alt="" className="w-full h-40 object-cover mb-2" onError={(e) => { e.target.style.display = "none"; }} />;
            })()}
            {hotel.address && (
              <p className="text-[10px] font-sans text-muted-foreground mb-3">{hotel.address}</p>
            )}
            {hotel.description && (
              <p className="text-sm text-muted-foreground leading-relaxed mb-4 max-w-xl">{hotel.description}</p>
            )}
            {amenities.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {amenities.map((a, i) => (
                  <span key={i} className="text-[9px] font-sans bg-cream-mid text-muted-foreground px-2.5 py-1">
                    {a}
                  </span>
                ))}
              </div>
            )}

            {/* Disclaimer */}
            <div className="bg-cream border border-border p-2.5 text-center mb-5">
              <p className="text-[9px] font-sans text-muted-foreground tracking-wide">
                THIS IS A PROPOSAL ONLY. NO ROOMS ARE BEING HELD. RATES AND CURRENCIES ARE SUBJECT TO AVAILABILITY AND MAY CHANGE.
              </p>
            </div>

            {/* Hotel-level dates (when dates_per_room is false) */}
            {(() => {
              if (hotel.static_rate || hotel.dates_per_room) return null;
              // Use hotel-level dates, or fallback to first room's dates
              const ci = hotel.check_in || hotel.rooms?.[0]?.check_in;
              const co = hotel.check_out || hotel.rooms?.[0]?.check_out;
              const n = hotel.nights || hotel.rooms?.[0]?.nights || 0;
              if (!ci) return null;
              return (
                <div className="flex gap-8 mb-5">
                  <div className="border-t-2 border-navy pt-2">
                    <p className="text-[9px] font-sans uppercase tracking-[0.1em] text-muted-foreground mb-1">Check in</p>
                    <p className="text-lg text-navy">{fmtDate(ci)}</p>
                    <p className="text-[10px] font-sans text-muted-foreground">{dayName(ci)}</p>
                  </div>
                  <div className="border-t-2 border-navy pt-2">
                    <p className="text-[9px] font-sans uppercase tracking-[0.1em] text-muted-foreground mb-1">Check out</p>
                    <p className="text-lg text-navy">{fmtDate(co)}</p>
                    <p className="text-[10px] font-sans text-muted-foreground">{dayName(co)}</p>
                  </div>
                  {n > 0 && (
                    <div className="border-t-2 border-gold pt-2">
                      <p className="text-[9px] font-sans uppercase tracking-[0.1em] text-muted-foreground mb-1">Duration</p>
                      <p className="text-lg text-navy">{n} Night{n > 1 ? "s" : ""}</p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Rooms */}
            {hotel.rooms?.map((room, roomIndex) => (
              <div key={room.id}>
                {/* Per-room dates (only when dates_per_room is true) */}
                {!hotel.static_rate && hotel.dates_per_room && room.check_in && (
                  <div className="flex gap-8 mb-5">
                    <div className="border-t-2 border-navy pt-2">
                      <p className="text-[9px] font-sans uppercase tracking-[0.1em] text-muted-foreground mb-1">Check in</p>
                      <p className="text-lg text-navy">{fmtDate(room.check_in)}</p>
                      <p className="text-[10px] font-sans text-muted-foreground">{dayName(room.check_in)}</p>
                    </div>
                    <div className="border-t-2 border-navy pt-2">
                      <p className="text-[9px] font-sans uppercase tracking-[0.1em] text-muted-foreground mb-1">Check out</p>
                      <p className="text-lg text-navy">{fmtDate(room.check_out)}</p>
                      <p className="text-[10px] font-sans text-muted-foreground">{dayName(room.check_out)}</p>
                    </div>
                  </div>
                )}

                {/* Room Card */}
                <div className="bg-cream p-4 border-l-2 border-gold mb-3">
                  <p className="text-[8px] font-sans uppercase tracking-[0.15em] text-muted-foreground mb-1">
                    Room {roomIndex + 1}
                  </p>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-base text-navy">{room.room_type}</span>
                    {!hotel.static_rate && room.nights > 0 && (
                      <span className="bg-navy text-white text-[10px] font-sans font-semibold px-2 py-0.5">
                        {room.nights}N
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] font-sans text-muted-foreground mb-3">
                    {room.adults} Adult{room.adults > 1 ? "s" : ""}
                    {room.children > 0 && `, ${room.children} ${room.children > 1 ? 'Children' : 'Child'}`}
                    {!hotel.static_rate && <> · {room.nights || "?"} Night{room.nights > 1 ? "s" : ""}</>}
                    {room.num_rooms > 1 && <> · {room.num_rooms} Rooms</>}
                  </p>

                  {/* Rate Table */}
                  <table className="w-full font-sans text-sm">
                    <tbody>
                      <tr>
                        <td className="py-1 text-muted-foreground">Room charges</td>
                        <td className="py-1 text-right font-semibold text-navy">
                          {formatCurrency(room.room_charge, hotel.currency)}
                        </td>
                      </tr>
                      {room.service_charge > 0 && (
                        <tr>
                          <td className="py-1 text-muted-foreground pl-3 text-xs">Service charge</td>
                          <td className="py-1 text-right text-navy">
                            {formatCurrency(room.service_charge, hotel.currency)}
                          </td>
                        </tr>
                      )}
                      {room.tax > 0 && (
                        <tr>
                          <td className="py-1 text-muted-foreground pl-3 text-xs">Tax (VAT/GST)</td>
                          <td className="py-1 text-right text-navy">
                            {formatCurrency(room.tax, hotel.currency)}
                          </td>
                        </tr>
                      )}
                      <tr className="border-t border-border">
                        <td className="pt-2 text-navy font-medium">
                          Proposed total per room{" "}
                          <span className="text-[9px] text-muted-foreground font-normal">incl. all taxes</span>
                        </td>
                        <td className="pt-2 text-right">
                          <div className="font-serif text-lg font-bold text-gold">
                            {formatCurrency(room.total, hotel.currency)}
                          </div>
                          {toINR(room.total, hotel.currency) && (
                            <div className="text-[9px] text-muted-foreground">
                              Approx. {fmtINR(toINR(room.total, hotel.currency))}
                            </div>
                          )}
                        </td>
                      </tr>
                      {room.num_rooms > 1 && (
                        <tr className="border-t border-dashed border-border">
                          <td className="pt-2 text-navy font-semibold text-sm">
                            Total for {room.num_rooms} rooms
                          </td>
                          <td className="pt-2 text-right">
                            <div className="font-serif text-base font-bold text-navy">
                              {formatCurrency(room.total * room.num_rooms, hotel.currency)}
                            </div>
                            {toINR(room.total * room.num_rooms, hotel.currency) && (
                              <div className="text-[9px] text-muted-foreground">
                                Approx. {fmtINR(toINR(room.total * room.num_rooms, hotel.currency))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {room.meal_plan && (
                    <p className="text-[10px] font-sans text-muted-foreground mt-3">
                      Meal plan: {room.meal_plan}
                    </p>
                  )}
                </div>
              </div>
            ))}
            
            {/* Per-Hotel Inclusions */}
            {hotel.inclusions && hotel.inclusions.length > 0 && (
              <div className="mt-4 p-4 bg-cream/50 border border-border">
                <p className="text-[9px] font-sans uppercase tracking-[0.12em] text-navy font-bold mb-2">
                  Inclusions at {hotel.hotel_name || 'this property'}
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {hotel.inclusions.map((inc, i) => (
                    <p key={i} className="text-xs font-sans text-muted-foreground">
                      · {inc}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Per-Hotel Cancellation Policy */}
            {hotel.cancellation_policy && (
              <div className="mt-3 p-4 bg-cream/50 border border-border">
                <p className="text-[9px] font-sans uppercase tracking-[0.12em] text-navy font-bold mb-1.5">
                  Cancellation Policy — {hotel.hotel_name || 'this property'}
                </p>
                <p className="text-xs font-sans text-muted-foreground leading-relaxed">
                  {hotel.cancellation_policy}
                </p>
              </div>
            )}

            {/* Gallery Page (after all hotel content) */}
            {(() => {
              const imgs = hotel.images || [];
              if (!imgs.length || !hotel.images_separate_page) return null;
              return (
                <div className="bg-navy p-6 mt-5 -mx-8 -mb-8">
                  <p className="text-[9px] font-sans uppercase tracking-[0.15em] text-gold mb-4">
                    {(hotel.hotel_name || "Property").toUpperCase()} — Gallery
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {imgs.slice(0, 9).map((img) => (
                      <img key={img.id} src={img.url} alt="" className="w-full aspect-square object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })}

      {/* Summary Comparison Table (only if 2+ hotels and show_summary enabled) */}
      {proposal.hotels?.length > 1 && proposal.show_summary !== false && (
        <div className="p-8 border-b border-border" data-testid="pdf-summary-table">
          <p className="text-[9px] font-sans uppercase tracking-[0.15em] font-bold text-navy mb-4 tracking-widest">
            Proposal Summary
          </p>
          <table className="w-full text-sm font-sans border-collapse">
            <thead>
              <tr className="border-b-2 border-navy">
                <th className="text-[8px] uppercase tracking-wider text-muted-foreground py-2 text-center w-8">#</th>
                <th className="text-[8px] uppercase tracking-wider text-muted-foreground py-2 text-left">Property</th>
                <th className="text-[8px] uppercase tracking-wider text-muted-foreground py-2 text-left">Room Category</th>
                <th className="text-[8px] uppercase tracking-wider text-muted-foreground py-2 text-center">Nights</th>
                <th className="text-[8px] uppercase tracking-wider text-muted-foreground py-2 text-center">Qty</th>
                <th className="text-[8px] uppercase tracking-wider text-muted-foreground py-2 text-right">Rate / Room</th>
              </tr>
            </thead>
            <tbody>
              {proposal.hotels.flatMap((hotel, idx) => {
                const nights = hotel.nights || hotel.rooms?.[0]?.nights || 0;
                return (hotel.rooms || []).map((room, ri) => (
                  <tr key={`${hotel.id}-${room.id}`} className={ri === hotel.rooms.length - 1 ? "border-b-2 border-navy" : "border-b border-stone-300"}>
                    {ri === 0 ? (
                      <td className="py-2.5 text-center font-bold text-gold text-sm border-b-2 border-navy" rowSpan={hotel.rooms.length}>{idx + 1}</td>
                    ) : null}
                    {ri === 0 ? (
                      <td className="py-2.5 border-b-2 border-navy" rowSpan={hotel.rooms.length}>
                        <span className="font-bold text-navy text-xs">{hotel.hotel_name}</span>
                        <br />
                        <span className="text-[9px] text-muted-foreground">{hotel.city}</span>
                      </td>
                    ) : null}
                    <td className="py-2.5 text-xs text-muted-foreground">{room.room_type || "—"}</td>
                    <td className="py-2.5 text-center text-xs text-muted-foreground">{room.nights || nights || "—"}</td>
                    <td className="py-2.5 text-center text-xs text-muted-foreground">{room.num_rooms || 1}</td>
                    <td className="py-2.5 text-right font-bold text-navy text-sm">{formatCurrency(room.total || 0, hotel.currency)}</td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Itinerary & Sightseeing */}
      {proposal.itineraries?.map((itin, iIdx) => {
        const tours = itin.tours || [];
        if (!tours.length && !itin.title) return null;
        const fmtCost = (amt, cur) => `${cur || itin.currency} ${(amt || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        return (
          <div key={itin.id} className="p-8 border-b border-border" data-testid={`preview-itinerary-${iIdx}`}>
            <div className="bg-gold/10 border-l-2 border-gold p-4 mb-4">
              <p className="text-[9px] font-sans uppercase tracking-[0.15em] text-gold mb-1">Itinerary {iIdx + 1}{itin.city ? ` — ${itin.city}` : ""}</p>
              <h2 className="text-lg text-navy">{itin.title || "Sightseeing & Activities"}</h2>
              <div className="flex gap-4 mt-1">
                {itin.adults > 0 && <span className="text-[10px] font-sans text-muted-foreground">{itin.adults} Guest{itin.adults !== 1 ? "s" : ""}</span>}
                {itin.cost_type === "per_person" && <span className="text-[10px] font-sans text-gold italic">Prices per person</span>}
              </div>
            </div>
            {tours.map((tour, tIdx) => (
              <div key={tour.id} className="bg-cream p-3 border-l-2 border-navy/20 mb-2">
                <div className="flex justify-between items-start">
                  <div>
                    {tour.day_date && <p className="text-[8px] font-sans uppercase tracking-wider text-gold font-bold mb-0.5">{tour.day_date}{tour.start_time ? ` · ${tour.start_time}${tour.end_time ? `–${tour.end_time}` : ""}` : ""}</p>}
                    <div className="flex items-center gap-2">
                      {tour.tour_type && tour.tour_type !== "activity" && (
                        <span className={`text-[7px] uppercase font-bold px-1.5 py-0.5 ${tour.tour_type === "transfer" ? "bg-navy/10 text-navy" : "bg-emerald-50 text-emerald-600"}`}>{tour.tour_type}</span>
                      )}
                      <p className="text-sm font-bold text-navy">{tour.name || `Activity ${tIdx + 1}`}</p>
                    </div>
                  </div>
                  {(itin.pricing_mode === "per_tour" || itin.pricing_mode === "both") && tour.cost > 0 && (
                    <div className="text-right">
                      <span className="text-sm font-bold text-gold font-serif">{fmtCost(tour.cost, tour.currency)}</span>
                      {itin.cost_type === "total" && itin.adults > 0 && (
                        <div className="text-[8px] text-muted-foreground italic">Total for {itin.adults} guest{itin.adults !== 1 ? "s" : ""}</div>
                      )}
                      {toINR(tour.cost, tour.currency || itin.currency) && (
                        <div className="text-[9px] text-muted-foreground">Approx. {fmtINR(toINR(tour.cost, tour.currency || itin.currency))}</div>
                      )}
                    </div>
                  )}
                </div>
                {tour.description && <p className="text-xs font-sans text-muted-foreground mt-1">{tour.description}</p>}
                <div className="flex gap-4 mt-1 flex-wrap">
                  {tour.duration && <span className="text-[10px] font-sans text-muted-foreground">{tour.duration}</span>}
                  {tour.pickup && <span className="text-[10px] font-sans text-muted-foreground">Pickup: {tour.pickup}</span>}
                </div>
                {tour.inclusions?.length > 0 && (
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {tour.inclusions.map((inc) => <span key={inc} className="text-[9px] bg-gold/10 text-gold px-1.5 py-0.5">{inc} included</span>)}
                  </div>
                )}
                {tour.notes && <p className="text-[10px] font-sans text-navy/60 italic mt-1">{tour.notes}</p>}
              </div>
            ))}
            {(itin.pricing_mode === "package" || itin.pricing_mode === "both") && itin.package_price > 0 && (
              <div className="mt-3 pt-3 border-t border-border flex justify-between items-baseline">
                <span className="text-sm font-semibold font-sans text-navy">Package Total</span>
                <div className="text-right">
                  <span className="text-lg font-bold font-serif text-gold">{fmtCost(itin.package_price, itin.currency)}</span>
                  {toINR(itin.package_price, itin.currency) && (
                    <div className="text-[9px] text-muted-foreground">Approx. {fmtINR(toINR(itin.package_price, itin.currency))}</div>
                  )}
                </div>
              </div>
            )}
            {itin.cancellation_policy && (
              <div className="mt-3 p-3 bg-cream/50 border border-border">
                <p className="text-[9px] font-sans uppercase tracking-[0.12em] text-navy font-bold mb-1">Cancellation Policy</p>
                <p className="text-xs font-sans text-muted-foreground">{itin.cancellation_policy}</p>
              </div>
            )}
          </div>
        );
      })}

      {/* Global Inclusions (backward compatibility) */}
      {getAllInclusions().length > 0 && (
        <div className="p-8 border-b border-border">
          <p className="text-[9px] font-sans uppercase tracking-[0.15em] text-muted-foreground mb-4">
            Package Inclusions
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            {getAllInclusions().map((inc, i) => (
              <p key={i} className="text-sm font-sans text-muted-foreground border-b border-cream-mid py-1.5">
                · {inc}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Policies & Terms */}
      {(proposal.guarantee_policy || proposal.terms?.length > 0) && (
        <div className="p-8 border-b border-border">
          {proposal.guarantee_policy && (
            <div className="mb-4">
              <p className="text-[9px] font-sans uppercase tracking-[0.12em] text-navy font-bold mb-1.5">
                Guarantee / Deposit Policy
              </p>
              <p className="text-sm font-sans text-muted-foreground leading-relaxed">
                {proposal.guarantee_policy}
              </p>
            </div>
          )}
          {proposal.terms?.length > 0 && (
            <div>
              <p className="text-[9px] font-sans uppercase tracking-[0.12em] text-navy font-bold mb-2">
                Terms & Conditions
              </p>
              {proposal.terms.map((term, i) => (
                <div key={i} className="flex gap-2 text-sm font-sans text-muted-foreground border-b border-cream-mid py-1.5">
                  <span className="text-gold font-bold min-w-[20px]">{i + 1}.</span>
                  <span>{term}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="bg-navy p-6 flex justify-between items-end">
        <div>
          <p className="text-white text-base mb-1">{proposal.consultant_name || "Pratyush Sareen"}</p>
          <p className="text-gold text-[10px] font-sans mb-1.5">
            {proposal.consultant_designation || "Director — Travel"} · {agencyName}
          </p>
          <p className="text-white/50 text-[10px] font-sans">
            {[proposal.consultant_phone, proposal.consultant_email].filter(Boolean).join("  ·  ")}
          </p>
        </div>
        <div className="text-right text-white/40 text-[10px] font-sans">
          <p>Ref: {proposal.reference}</p>
          <p>{fmtDate(proposal.proposal_date)}</p>
        </div>
      </div>
    </div>
  );
};

export default ProposalPreview;
